/**
 * VerifyEmailV2 — v2 reskin of auth/VerifyEmail (UI-v2 Phase 1e).
 *
 * Logic verbatim from v1 (effect + navigate). VerifyEmail is unusual —
 * it has no form, only loading/error/success states. The v2 shell wraps
 * each state branch. Theme scope comes from AuthShellV2's root div.
 */
import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useVerifyEmail } from "@/hooks/queries/auth/useVerifyEmail";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";

// ── Spinner (inline, no dep on common/Spinner so this stays self-contained) ──
function SpinnerV2({ size = "md" }: { size?: "sm" | "md" | "lg" }): JSX.Element {
  const px = size === "lg" ? "40px" : size === "sm" ? "16px" : "24px";
  const border = size === "lg" ? "4px" : "2px";
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        width: px,
        height: px,
        borderWidth: border,
        borderStyle: "solid",
        borderColor: "var(--accent-primary) transparent var(--accent-primary) var(--accent-primary)",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}

export default function VerifyEmailV2(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const verify = useVerifyEmail();
  const hasRun = useRef(false);

  const token = searchParams.get("token") ?? "";

  useEffect(() => {
    if (!token || hasRun.current) return;
    hasRun.current = true;
    verify.mutate(
      { token },
      {
        onSuccess: (data) => {
          navigate(`/${data.tenant_slug}/login`, { replace: true });
        },
      }
    );
  }, [token, verify, navigate]);

  if (!token) {
    return (
      <AuthShellV2 title="Email verification">
        <p className="text-sm text-text-secondary">Invalid verification link.</p>
      </AuthShellV2>
    );
  }

  if (verify.isPending || verify.isIdle) {
    return (
      <AuthShellV2 title="Verifying…">
        <div className="flex flex-col items-center gap-3 py-4">
          <SpinnerV2 size="lg" />
          <p className="text-sm text-text-secondary">Verifying your email…</p>
        </div>
      </AuthShellV2>
    );
  }

  if (verify.isError) {
    const code = verify.error.error;
    if (code === "already_verified") {
      return (
        <AuthShellV2 title="Already verified">
          <p className="mb-4 text-sm text-text-secondary">
            Your email is already verified.
          </p>
          <a
            href="/login"
            className="text-sm text-text-link underline hover:text-text-link-hover"
          >
            Sign in
          </a>
        </AuthShellV2>
      );
    }
    return (
      <AuthShellV2 title="Link expired">
        <p className="mb-4 text-sm text-text-secondary">
          This link is expired. Request a new one or email support@certainhr.ai.
        </p>
        <a
          href="mailto:support@certainhr.ai"
          className="text-sm text-text-link underline hover:text-text-link-hover"
        >
          Contact support
        </a>
      </AuthShellV2>
    );
  }

  return (
    <AuthShellV2 title="Verified">
      <div className="flex justify-center py-4">
        <SpinnerV2 size="lg" />
      </div>
    </AuthShellV2>
  );
}
